import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import LightweightChart from "@/components/LightweightChart";
import SymbolSearch from "@/components/SymbolSearch";
import TopSetups from "@/components/TopSetups";
import AdminControlPanel from "@/components/AdminControlPanel";
import AccuracyBanner from "@/components/AccuracyBanner";
import RecentClosedSignals from "@/components/RecentClosedSignals";
import QuantShadowPanel from "@/components/QuantShadowPanel";
import LevelDecayStudy from "@/components/LevelDecayStudy";
import SignalChimes from "@/components/SignalChimes";
import GlobalShockBanner from "@/components/GlobalShockBanner";
import BrokerStatusChip from "@/components/BrokerStatusChip";
import TradeTerminal from "@/components/TradeTerminal";
import LiveTicker from "@/components/LiveTicker";
import ExpiryAlertBanner from "@/components/ExpiryAlertBanner";
import TradeCloseModal from "@/components/TradeCloseModal";
import OptionsLabPage from "@/pages/OptionsLabPage";
import StrategyBacktest from "@/components/StrategyBacktest";
import StrategyRecordBanner from "@/components/StrategyRecordBanner";
import { NewsPanel } from "@/components/dashboard/NewsPanel";
import { SignalPanel } from "@/components/dashboard/SignalPanel";
import { PlanBanners } from "@/components/dashboard/PlanBanners";
import { segmentOf } from "@/lib/segments";
import { randomSignalLoadingMsg, randomNewsLoadingMsg } from "@/lib/loadingMessages";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, X, Star, ExternalLink } from "lucide-react";

export { segmentOf };

export default function Dashboard() {
  const { user, loading: authLoading, checkAuth } = useAuth();
  const nav = useNavigate();

  const [watchlist, setWatchlist] = useState([]);
  const [selected, setSelected] = useState(null);
  const [signal, setSignal] = useState(null);
  // Per-resource tokens so signal and news don't invalidate each other.
  const signalTokenRef = useRef(0);
  const newsTokenRef = useRef(0);
  const [news, setNews] = useState(null);
  const [loadingSig, setLoadingSig] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(() => randomSignalLoadingMsg());
  const [newsLoadingMsg, setNewsLoadingMsg] = useState(() => randomNewsLoadingMsg());
  const [tab, setTab] = useState("All");
  const [searchOpen, setSearchOpen] = useState(false);
  const [page, setPage] = useState("dashboard"); // "dashboard" | "options-lab" | "backtest"
  const [mode, setMode] = useState(() => localStorage.getItem("tiq_mode") || "day");
  const [strategy, setStrategy] = useState(() => localStorage.getItem("tiq_strategy") || "s1");
  const [candleState, setCandleState] = useState(null);
  const [alertsActive, setAlertsActive] = useState(0);
  const [segment, setSegment] = useState("indian");

  useEffect(() => { localStorage.setItem("tiq_mode", mode); }, [mode]);
  useEffect(() => { localStorage.setItem("tiq_strategy", strategy); }, [strategy]);

  const STRAT_SYMBOLS = ["NIFTY", "BANKNIFTY", "SENSEX"];
  const effStrategy = mode === "day" && STRAT_SYMBOLS.includes(selected?.symbol) ? strategy : null;

  // Live 5-min candle verdict: entry confirmed (S1) / contra triggered (S2)
  useEffect(() => {
    setCandleState(null);
    if (!signal || mode !== "day" || signal.status === "no_trade" || !selected
        || !STRAT_SYMBOLS.includes(selected.symbol)
        || typeof signal.entry_price !== "number" || !signal.trend) return;
    let dead = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/signal/${encodeURIComponent(selected.symbol)}/candle-state`, {
          params: { entry: signal.entry_price, trend: signal.trend, generated_at: signal.generated_at || "" },
        });
        if (!dead && data?.ok) setCandleState(data);
      } catch { /* transient */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { dead = true; clearInterval(id); };
  }, [signal, selected, mode]);

  // Lightweight poll of active alerts so the Trade Setup panel can show
  // a per-trade warning overlay alongside the top banner.
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await api.get("/alerts/active");
        if (!cancel) setAlertsActive((data?.items || []).length);
      } catch (e) {
        console.warn("active alerts poll failed:", e?.message || e);
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancel = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!authLoading && !user) nav("/login");
  }, [authLoading, user, nav]);

  const loadWatchlist = useCallback(async () => {
    try {
      const { data } = await api.get("/watchlist");
      setWatchlist(data);
      setSelected((cur) => cur || data[0] || null);
    } catch (e) {
      console.error("watchlist load failed:", e?.message || e);
    }
  }, []);

  useEffect(() => { loadWatchlist(); }, [loadWatchlist]);
  useEffect(() => {
    window.addEventListener("universe:changed", loadWatchlist);
    return () => window.removeEventListener("universe:changed", loadWatchlist);
  }, [loadWatchlist]);

  // global cmd+k (admin only — the list is the admin-curated scanner universe)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (user?.is_admin) setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user?.is_admin]);

  const fetchSignal = useCallback(async (inst) => {
    const token = ++signalTokenRef.current;
    const expectedSymbol = inst.symbol;
    const expectedMode = mode;
    setLoadingMsg(randomSignalLoadingMsg());
    setLoadingSig(true); setSignal(null);
    try {
      const { data } = await api.get(`/signal/${encodeURIComponent(inst.symbol)}`, {
        params: { tv_symbol: inst.tv_symbol, name: inst.name, category: inst.category, mode },
      });
      // Discard if a newer fetch has started OR the active symbol/mode has changed.
      if (token !== signalTokenRef.current) return;
      if ((data?.symbol || "").toUpperCase() !== (expectedSymbol || "").toUpperCase()) return;
      if (data?.mode && data.mode !== expectedMode) return;
      setSignal(data);
    } catch (e) {
      console.error("signal fetch failed:", e?.message || e);
      if (token === signalTokenRef.current) toast.error("Could not fetch signal");
    } finally {
      if (token === signalTokenRef.current) setLoadingSig(false);
    }
  }, [mode]);

  const fetchNews = useCallback(async (inst) => {
    const token = ++newsTokenRef.current;
    const expected = inst.symbol;
    setNewsLoadingMsg(randomNewsLoadingMsg());
    setLoadingNews(true); setNews(null);
    try {
      const { data } = await api.get(`/news/${encodeURIComponent(inst.symbol)}`, {
        params: { tv_symbol: inst.tv_symbol, name: inst.name, category: inst.category },
      });
      if (token !== newsTokenRef.current) return;
      if ((data?.symbol || "").toUpperCase() !== (expected || "").toUpperCase()) return;
      setNews(data);
    } catch (e) {
      console.error("news fetch failed:", e?.message || e);
      if (token === newsTokenRef.current) toast.error("Could not fetch news");
    } finally {
      if (token === newsTokenRef.current) setLoadingNews(false);
    }
  }, []);

  // Auto-refresh: signal every 2 min, news every 5 min (matches server cache TTLs).
  // No manual refresh button for clients — system manages cadence to control LLM credits.
  useEffect(() => {
    if (!selected) return;
    fetchSignal(selected); fetchNews(selected);
    const sigId = setInterval(() => fetchSignal(selected), 120000);
    const newsId = setInterval(() => fetchNews(selected), 300000);
    return () => { clearInterval(sigId); clearInterval(newsId); };
  }, [selected, fetchSignal, fetchNews]);

  const scope = user?.market_scope || "all";
  const effSegment = scope === "indian" ? "indian" : segment;
  const segWatchlist = useMemo(
    () => watchlist.filter((i) => segmentOf(i.symbol, i.category, i.tv_symbol) === effSegment),
    [watchlist, effSegment]);
  const categories = useMemo(() => ["All", ...Array.from(new Set(segWatchlist.map((i) => i.category)))], [segWatchlist]);
  const filtered = useMemo(() => tab === "All" ? segWatchlist : segWatchlist.filter((i) => i.category === tab), [tab, segWatchlist]);

  // Keep selection inside the active segment (also corrects async watchlist load, e.g. a
  // persisted global symbol like USDCHF must never render under the Indian tab)
  useEffect(() => {
    if (selected && segmentOf(selected.symbol, selected.category, selected.tv_symbol) !== effSegment) {
      setSelected(segWatchlist[0] || null);
      setTab("All");
    }
    // Options Lab (DHAN execution) is Indian-only — leave it when switching to Global.
    if (effSegment !== "indian" && page === "options-lab") setPage("dashboard");
  }, [effSegment, selected, segWatchlist, page]);

  const addToWatchlist = async (item) => {
    try {
      const payload = {
        symbol: item.symbol.toUpperCase(),
        tv_symbol: item.tv_symbol,
        name: item.name,
        category: item.category || "Custom",
      };
      const { data } = await api.post("/watchlist", payload);
      setWatchlist((prev) => {
        if (prev.some((p) => p.symbol === data.symbol)) return prev;
        return [data, ...prev];
      });
      toast.success(`${data.symbol} added to watchlist`);
      setSelected(data);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not add to watchlist");
    }
  };

  const selectAndAddIfNew = (item) => {
    const exists = watchlist.find((w) => w.symbol === item.symbol.toUpperCase());
    if (exists) {
      setSelected(exists);
    } else {
      addToWatchlist(item);
    }
  };

  const removeFromWatchlist = async (sym) => {
    try {
      await api.delete(`/watchlist/${encodeURIComponent(sym)}`);
      setWatchlist((prev) => prev.filter((p) => p.symbol !== sym));
      if (selected?.symbol === sym) setSelected(watchlist[0] || null);
      toast.success(`${sym} removed`);
    } catch (e) {
      console.error("watchlist remove failed:", e?.message || e);
      toast.error("Could not remove");
    }
  };

  const upgrade = async (plan) => {
    try {
      await api.post("/subscription/trial", { plan });
      toast.success(`3-day free trial started — Pro features unlocked. You can cancel anytime before charge.`);
      await checkAuth();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start trial";
      toast.error(typeof msg === "string" ? msg : "Could not start trial");
    }
  };

  const cancelPlan = async () => {
    if (!window.confirm("Cancel your subscription? If still in the 3-day trial, you won't be charged.")) return;
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success(data.outcome === "trial_cancelled_no_charge" ? "Trial cancelled — no charge applied." : "Plan cancelled — access continues until expiry.");
      await checkAuth();
    } catch (e) {
      console.error("cancel plan failed:", e?.message || e);
      toast.error("Could not cancel");
    }
  };

  if (authLoading || !user) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center font-mono text-sm uppercase tracking-widest">◢ Loading…</div>;
  }

  const isPro = user?.plan === "pro";
  const isAdmin = !!user?.is_admin;


  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar variant="dashboard" />

      {/* SL-HIT / TRADE-CLOSED modal — loud notification so users never miss a close */}
      <TradeCloseModal />

      <AdminControlPanel user={user} />

      {/* Global shock alerts — sticky at top */}
      <GlobalShockBanner />

      {/* Live ticker of major Indian + Global symbols */}
      <LiveTicker scope={scope} />

      {/* Market segment switcher — Indian-only subscribers never see Global */}
      <div className="border-b border-zinc-900 bg-[#0a0a0a] px-4 md:px-6 flex items-center gap-1" data-testid="segment-tabs">
        <button
          onClick={() => setSegment("indian")}
          className={`text-xs uppercase tracking-widest py-2.5 px-4 border-b-2 font-bold transition-colors ${effSegment === "indian" ? "border-[#F59E0B] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
          data-testid="segment-tab-indian"
        >
          🇮🇳 Indian Markets
        </button>
        {scope !== "indian" && (
          <button
            onClick={() => setSegment("global")}
            className={`text-xs uppercase tracking-widest py-2.5 px-4 border-b-2 font-bold transition-colors ${effSegment === "global" ? "border-[#3B9BFF] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
            data-testid="segment-tab-global"
          >
            🌍 Global Markets
          </button>
        )}
        <span className="ml-auto flex items-center gap-2">
          <SignalChimes />
          <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600 hidden sm:inline">
            {scope === "indian" ? "Plan: Indian Markets" : "Plan: All Markets"}
          </span>
        </span>
      </div>

      {/* Options expiry-day alert (Indian indices, IST-anchored) */}
      {effSegment === "indian" && <ExpiryAlertBanner />}

      {/* Broker connection journey — the SINGLE place to connect / update / disconnect DHAN */}
      {(isPro || isAdmin) && effSegment === "indian" && <BrokerStatusChip />}

      {/* Verified accuracy banner — split per segment */}
      {effSegment === "indian"
        ? <StrategyRecordBanner onOpen={isPro ? () => setPage("backtest") : undefined} />
        : <AccuracyBanner segment={effSegment} />}

      {/* Top setups widget — scoped to the active segment */}
      <TopSetups segment={effSegment} onPick={(s) => {
        const exists = watchlist.find((w) => w.symbol === s.symbol);
        setSelected(exists || { symbol: s.symbol, tv_symbol: s.tv_symbol, name: s.name, category: s.category });
      }} />

      {/* Search + Watchlist strip */}
      <div className="border-b border-zinc-900 px-4 md:px-6" data-testid="instrument-strip">
        <div className="flex items-center gap-3 py-2 flex-wrap">
          <div className="inline-flex border border-zinc-800" data-testid="mode-toggle">
            <button onClick={() => setMode("day")} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${mode === "day" ? "bg-[#007AFF] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="mode-day">Day Trading</button>
            <button onClick={() => setMode("swing")} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${mode === "swing" ? "bg-[#007AFF] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="mode-swing">Swing Trading</button>
          </div>
          {mode === "day" && (isPro || isAdmin) && STRAT_SYMBOLS.includes(selected?.symbol) && (
            <div className="inline-flex border border-zinc-800" data-testid="strategy-toggle">
              <button onClick={() => setStrategy("s1")} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${strategy === "s1" ? "bg-[#A855F7] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="strategy-s1">Strategy 1</button>
              <button onClick={() => setStrategy("s2")} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${strategy === "s2" ? "bg-[#A855F7] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="strategy-s2">Strategy 2</button>
            </div>
          )}
          {isAdmin && (
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 px-3 py-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-white"
              data-testid="open-search-btn"
            >
              <Search className="h-3 w-3" /> Add to scanner universe <span className="text-zinc-700 font-mono ml-2">⌘K</span>
            </button>
          )}
          <div className="flex gap-1 items-center overflow-x-auto">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setTab(c)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold transition-colors whitespace-nowrap ${
                  tab === c ? "text-white border-b-2 border-[#007AFF]" : "text-zinc-500 hover:text-white"
                }`}
                data-testid={`cat-tab-${c}`}
              >{c}</button>
            ))}
          </div>
        </div>

        <div className="flex gap-1 items-center overflow-x-auto pb-2">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-600 font-mono pr-2 shrink-0">
            <Star className="h-3 w-3" /> Watchlist
          </div>
          {filtered.length === 0 && (
            <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest px-2">
              {isAdmin ? "Empty — add a symbol via ⌘K or Admin › AI Cost Controls" : "No symbols in this segment yet"}
            </div>
          )}
          {filtered.map((i) => (
            <div
              key={i.symbol}
              className={`group inline-flex items-stretch ${
                selected?.symbol === i.symbol ? "border border-[#007AFF]" : "border border-zinc-800 hover:border-zinc-600"
              }`}
            >
              <button
                onClick={() => setSelected(i)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest font-mono whitespace-nowrap transition-colors ${
                  selected?.symbol === i.symbol ? "bg-[#007AFF] text-white" : "text-zinc-300 hover:text-white"
                }`}
                data-testid={`inst-${i.symbol}`}
              >{i.symbol}</button>
              {isAdmin && (
                <button
                  onClick={() => removeFromWatchlist(i.symbol)}
                  className="px-1.5 text-zinc-700 hover:text-[#EF4444] opacity-0 group-hover:opacity-100 transition-opacity"
                  title={`Remove ${i.symbol} from scanner universe`}
                  data-testid={`inst-remove-${i.symbol}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade / trial / active-plan banners */}
      <PlanBanners user={user} isPro={isPro} upgrade={upgrade} cancelPlan={cancelPlan} />

      {/* Page tab switcher */}
      <div className="border-b border-zinc-900 bg-[#0a0a0a] px-4 md:px-6 flex items-center gap-4">
        <button
          onClick={() => setPage("dashboard")}
          className={`text-xs uppercase tracking-widest py-3 border-b-2 transition-colors ${page === "dashboard" ? "border-[#007AFF] text-white font-bold" : "border-transparent text-zinc-500 hover:text-white"}`}
          data-testid="page-tab-dashboard"
        >
          Dashboard
        </button>
        {isPro && effSegment === "indian" && (
          <button
            onClick={() => setPage("options-lab")}
            className={`text-xs uppercase tracking-widest py-3 border-b-2 transition-colors flex items-center gap-2 ${page === "options-lab" ? "border-[#007AFF] text-white font-bold" : "border-transparent text-zinc-500 hover:text-white"}`}
            data-testid="page-tab-options-lab"
          >
            Options Lab <span className="text-[9px] px-1.5 py-0.5 bg-[#007AFF]/15 text-[#007AFF] border border-[#007AFF]/40 font-mono">NEW</span>
          </button>
        )}
        {isPro && (
          <button
            onClick={() => setPage("backtest")}
            className={`text-xs uppercase tracking-widest py-3 border-b-2 transition-colors flex items-center gap-2 ${page === "backtest" ? "border-[#007AFF] text-white font-bold" : "border-transparent text-zinc-500 hover:text-white"}`}
            data-testid="page-tab-backtest"
          >
            Backtest <span className="text-[9px] px-1.5 py-0.5 bg-[#007AFF]/15 text-[#007AFF] border border-[#007AFF]/40 font-mono">NEW</span>
          </button>
        )}
      </div>

      {page === "options-lab" && isPro ? (
        <div className="p-4 md:p-6">
          <OptionsLabPage />
        </div>
      ) : page === "backtest" && isPro ? (
        <div className="p-4 md:p-6">
          <StrategyBacktest isAdmin={isAdmin} />
        </div>
      ) : (
      <>
      {/* Main grid */}
      <main className="p-2 md:p-4 grid grid-cols-1 lg:grid-cols-12 gap-4 auto-rows-min">
        {/* Left column = chart + news stacked. News fills the empty space below
            the chart when the right sidebar is taller. */}
        <div className="col-span-12 lg:col-span-8 xl:col-span-9 flex flex-col gap-4">
          {/* Chart */}
          <section className="bg-[#121212] border border-zinc-800 relative overflow-hidden" data-testid="chart-panel">
          <div className="border-b border-zinc-800 px-4 py-2 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">{selected?.category}</div>
              <div className="font-heading text-xl font-bold uppercase tracking-tight" data-testid="chart-title">{selected?.name} <span className="text-[#007AFF] font-mono">{selected?.symbol}</span></div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {selected?.tv_symbol && (
                <a
                  href={`https://www.tradingview.com/chart/?symbol=${encodeURIComponent(selected.tv_symbol)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#007AFF] transition-colors inline-flex items-center gap-1.5"
                  data-testid="open-in-tradingview"
                >
                  Open in TradingView <ExternalLink className="h-3 w-3" />
                </a>
              )}
              <div className="text-xs font-mono text-zinc-500 hidden md:block">5m · Day Trade</div>
            </div>
          </div>

          {selected && <LightweightChart instrument={selected} signal={signal} strategy={effStrategy} candleState={candleState} />}
        </section>

        {/* Live Trade Terminal — DHAN positions + order book (Indian segment only) */}
        {(isPro || isAdmin) && effSegment === "indian" && <TradeTerminal />}

        {/* News feed — sits directly below the chart in the left column so it
            fills the empty area created when the right sidebar is taller. */}
        <NewsPanel
          news={news}
          loadingNews={loadingNews}
          newsLoadingMsg={newsLoadingMsg}
          selected={selected}
          isPro={isPro}
          isAdmin={isAdmin}
          onRefresh={() => selected && fetchNews(selected)}
        />

        {/* Admin-only: Quant Engine v2 — full-width landscape placement */}
        {isAdmin && <QuantShadowPanel />}
        {isAdmin && <LevelDecayStudy />}
      </div>

        {/* Side panels */}
        <aside className="col-span-12 lg:col-span-4 xl:col-span-3 flex flex-col gap-4">
          <SignalPanel
            signal={signal}
            loadingSig={loadingSig}
            loadingMsg={loadingMsg}
            selected={selected}
            mode={mode}
            strategy={effStrategy}
            candleState={candleState}
            isPro={isPro}
            isAdmin={isAdmin}
            alertsActive={alertsActive}
            onRefresh={() => selected && fetchSignal(selected)}
            upgrade={upgrade}
          />

          {/* Admin-only: backtest audit table */}
          {isAdmin && <RecentClosedSignals />}
        </aside>
      </main>
      </>
      )}

      <div className="px-4 md:px-6 py-6 text-[10px] uppercase tracking-widest text-zinc-600 font-mono">
        ◢ ProTradeIQ v1.0 · For educational use only · Not investment advice · <Link to="/" className="hover:text-white">Home</Link>
      </div>

      <SymbolSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={selectAndAddIfNew}
        onAddToWatchlist={addToWatchlist}
      />
    </div>
  );
}
